import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { UserMinus, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import {
  getExitInterviewEligibleEmployees,
  assignExitInterview,
} from "@/services/performanceService";

export default function AssignExitInterviewDialog({
  templates = [],
  onCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  hideTrigger = false,
}) {
  const navigate = useNavigate();
  const { can } = usePermission();
  // Memoize so typing doesn't re-invoke can() on every keystroke.
  const { canCreate } = useMemo(() => createCanHelpers(can), [can]);
  const canAssign = useMemo(() => canCreate("performance_management"), [canCreate]);

  // Controlled when a parent passes `open`/`onOpenChange` (e.g. driven from the
  // Performance "Actions" dropdown); otherwise self-managed with its own trigger.
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    if (!isControlled) setInternalOpen(next);
    controlledOnOpenChange?.(next);
  };
  const [eligibleEmployees, setEligibleEmployees] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [employeeId, setEmployeeId] = useState(""); // Employee Mongo _id
  const [templateId, setTemplateId] = useState("");
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");
  const [assigning, setAssigning] = useState(false);

  const resetForm = () => {
    setEmployeeId("");
    setTemplateId("");
    setEmployeeSearchQuery("");
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) resetForm();
  };

  // Fetch the eligible list fresh each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    let ignore = false;
    (async () => {
      setLoadingEligible(true);
      try {
        const response = await getExitInterviewEligibleEmployees();
        if (!ignore) setEligibleEmployees(response.data || []);
      } catch (error) {
        console.error("Error loading eligible employees:", error);
        if (!ignore) {
          toast.error(
            error.response?.data?.message || "Failed to load eligible employees",
          );
          setEligibleEmployees([]);
        }
      } finally {
        if (!ignore) setLoadingEligible(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [open]);

  const selectedEmployee = eligibleEmployees.find(
    (emp) => String(emp._id) === employeeId,
  );

  const handleAssign = async () => {
    if (!canCreate("performance_management", true)) return;

    if (!employeeId || !templateId) {
      toast.error("Please select an employee and a template");
      return;
    }

    try {
      setAssigning(true);
      const response = await assignExitInterview(employeeId, templateId);
      toast.success(response.message || "Exit interview assigned successfully");
      setOpen(false);
      resetForm();
      onCreated?.();
    } catch (error) {
      console.error("Error assigning exit interview:", error);
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to assign exit interview",
      );
    } finally {
      setAssigning(false);
    }
  };

  const filteredEmployees = eligibleEmployees.filter(
    (emp) =>
      (emp.name || "").toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
      (emp.employee_id || "")
        .toLowerCase()
        .includes(employeeSearchQuery.toLowerCase()),
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={!canAssign ? "opacity-70 cursor-not-allowed" : ""}
            disabled={!canAssign}
            data-testid="assign-exit-interview-btn"
          >
            <UserMinus className="mr-1.5 h-4 w-4" /> Assign Exit Interview
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Assign Exit Interview</DialogTitle>
          <DialogDescription>
            Assign an exit interview to an employee whose exit process has
            already been initiated.
          </DialogDescription>
        </DialogHeader>

        {loadingEligible ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Loading eligible employees...
          </div>
        ) : eligibleEmployees.length === 0 ? (
          <div className="py-8 px-4 text-center">
            <UserMinus className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground mb-1">
              No eligible employees
            </p>
            <p className="text-sm text-muted-foreground">
              Employees become eligible once their exit process is initiated
              and they have no active exit interview.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Select Employee *</Label>
              <Select
                value={employeeId}
                onValueChange={(value) => {
                  setEmployeeId(value);
                  setEmployeeSearchQuery("");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employee..." />
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2" onClick={(e) => e.stopPropagation()}>
                    <Input
                      placeholder="Search employee..."
                      value={employeeSearchQuery}
                      onChange={(e) => {
                        e.stopPropagation();
                        setEmployeeSearchQuery(e.target.value);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="h-8"
                    />
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    {filteredEmployees.map((emp) => (
                      <SelectItem key={emp._id} value={String(emp._id)}>
                        {emp.name} ({emp.employee_id})
                      </SelectItem>
                    ))}
                    {filteredEmployees.length === 0 && (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No employee found.
                      </div>
                    )}
                  </div>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Exit Interview Template *</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select template..." />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const filteredTemplates = Array.isArray(templates)
                      ? templates.filter((t) => t.type === "Exit Interview")
                      : [];

                    if (filteredTemplates.length === 0) {
                      return (
                        <div className="py-6 px-4 text-center">
                          <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mb-2">
                            No Exit Interview templates available
                          </p>
                          <Button
                            variant="link"
                            className="text-xs h-auto p-0"
                            onClick={() => navigate("/template-builder")}
                          >
                            Create template →
                          </Button>
                        </div>
                      );
                    }

                    return filteredTemplates.map((t) => (
                      <SelectItem key={t._id || t.id} value={t._id || t.id}>
                        {t.name}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>

            {selectedEmployee && (
              <div className="grid gap-2">
                <Label>Due Date (Last Working Day)</Label>
                <Input
                  readOnly
                  disabled
                  value={
                    selectedEmployee.last_working_day
                      ? new Date(
                          selectedEmployee.last_working_day,
                        ).toLocaleDateString()
                      : "N/A"
                  }
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            onClick={handleAssign}
            disabled={assigning || !employeeId || !templateId}
          >
            {assigning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              "Assign Exit Interview"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
